import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Subject } from 'rxjs';
import nacl from 'tweetnacl';
import bs58 from 'bs58';
// Import explicite plutôt qu'un polyfill global de `Buffer` (absent de polyfills.ts) — même
// convention que solana-bridge.service.ts ; le paquet `buffer` est déjà une dépendance transitive
// de @solana/web3.js.
import { Buffer } from 'buffer';

// Connexion Solana pour Phantom / Solflare **mobile**, via leurs "universal links" chiffrés
// (protocole documenté identique pour les deux : x25519 + nacl.box).
//
// POURQUOI : SolanaBridgeService ne sait dialoguer qu'avec un provider INJECTÉ
// (window.phantom.solana / window.solflare) — présent uniquement sur navigateur desktop avec
// l'extension, ou dans le navigateur intégré du wallet. Dans un navigateur mobile classique et
// dans l'app native (WebView Capacitor), il n'y a aucune injection → connexion impossible. Les
// deeplinks sont la voie officielle Phantom/Solflare pour ces contextes.
//
// Telegram Mini App : NON couvert ici. Le retour du deeplink se fait vers une page web ouverte
// hors de Telegram, sans canal de retour vers la WebView de la mini-app (le paramètre startapp est
// trop court pour la réponse chiffrée). Nécessiterait un relais backend — hors périmètre. Le
// dispatcher (SolanaBridgeService) détecte ce cas et affiche un message adapté.
//
// FLUX WEB (navigateur mobile) : on ouvre le deeplink dans un NOUVEL onglet (window.open) pour ne
// pas détruire l'état de la page d'origine. La page /solana-callback qui reçoit la réponse la
// réécrit dans localStorage puis se ferme ; la page d'origine écoute l'évènement `storage`.
// Repli si window.open est bloqué : navigation directe + réhydratation au retour (voir
// SolanaBridgeService.getPersistedConnection).
//
// FLUX NATIF (Capacitor) : Browser.open (onglet in-app) → Phantom → retour sur
// `irisapp://solana-callback` capté par app.component.ts (App.addListener 'appUrlOpen'). Le
// contexte JS de la WebView survit, la promesse en attente est résolue directement.

export type SolanaWallet = 'phantom' | 'solflare';

interface PendingRequest {
  wallet: SolanaWallet;
  phase: 'connect' | 'sign';
  dappSecretKey: string; // bs58
  nonce?: string;        // bs58 — pour la phase 'sign' (déchiffrement de la réponse)
  reqId: string;
  returnUrl: string;     // route in-app à restaurer si le contexte a été détruit (repli navigation directe)
  createdAt: number;
}

interface DeeplinkSession {
  wallet: SolanaWallet;
  publicKey: string;
  session: string;       // jeton opaque renvoyé par le wallet, à repasser à chaque signature
  sharedSecret: string;  // bs58
  dappSecretKey: string; // bs58 — paire de clés de la connexion, à réutiliser pour chaque requête
}

const PENDING_KEY = 'solana_dl_pending';
const SESSION_KEY = 'solana_dl_session';
// Pont localStorage pour le flux web multi-onglets — la page /solana-callback y écrit la réponse.
export const SOLANA_DL_RESULT_KEY = 'solana_dl_result';

const UL_BASE: Record<SolanaWallet, string> = {
  phantom:  'https://phantom.app/ul/v1',
  solflare: 'https://solflare.com/ul/v1',
};

@Injectable({ providedIn: 'root' })
export class SolanaDeeplinkService {
  // Émis quand une réponse de connexion valide est traitée (contexte JS survivant : natif, ou
  // onglet d'origine web via l'évènement storage). Charge utile = clé publique base58.
  readonly connected$ = new Subject<string>();
  // Émis quand une réponse signAndSendTransaction est traitée. Charge utile = signature (= txid).
  readonly signature$ = new Subject<{ reqId: string; signature: string }>();
  // Émis sur erreur renvoyée par le wallet (refus, etc.).
  readonly failed$ = new Subject<{ phase: 'connect' | 'sign'; message: string }>();

  constructor() {
    // Flux web : la page /solana-callback réécrit la réponse ici avant de se fermer.
    window.addEventListener('storage', (e) => {
      if (e.key === SOLANA_DL_RESULT_KEY && e.newValue) {
        try {
          const params = JSON.parse(e.newValue) as Record<string, string>;
          localStorage.removeItem(SOLANA_DL_RESULT_KEY);
          void this.resolveCallback(params);
        } catch { /* ignore */ }
      }
    });
  }

  private get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  private redirectLink(): string {
    return this.isNative
      ? 'irisapp://solana-callback'
      : `${window.location.origin}/solana-callback`;
  }

  hasActiveSession(): Promise<DeeplinkSession | null> {
    return Preferences.get({ key: SESSION_KEY }).then(r => {
      if (!r.value) return null;
      try { return JSON.parse(r.value) as DeeplinkSession; } catch { return null; }
    });
  }

  async clearSession(): Promise<void> {
    await Preferences.remove({ key: SESSION_KEY });
    await Preferences.remove({ key: PENDING_KEY });
  }

  // Route in-app à restaurer après un retour de deeplink qui a détruit le contexte JS (repli
  // navigation directe). Non consommée : juste lue par la page /solana-callback.
  async pendingReturnUrl(): Promise<string | null> {
    const r = await Preferences.get({ key: PENDING_KEY });
    if (!r.value) return null;
    try { return (JSON.parse(r.value) as PendingRequest).returnUrl ?? null; } catch { return null; }
  }

  // ── Connexion ──────────────────────────────────────────────────────────────

  async startConnect(wallet: SolanaWallet): Promise<void> {
    const kp = nacl.box.keyPair();
    const reqId = this.newReqId();
    await this.savePending({
      wallet,
      phase: 'connect',
      dappSecretKey: bs58.encode(kp.secretKey),
      reqId,
      returnUrl: window.location.pathname + window.location.search,
      createdAt: Date.now(),
    });

    const params = new URLSearchParams({
      dapp_encryption_public_key: bs58.encode(kp.publicKey),
      cluster: 'mainnet-beta',
      app_url: window.location.origin,
      redirect_link: this.redirectLink(),
    });
    this.openUrl(`${UL_BASE[wallet]}/connect?${params.toString()}`);
  }

  // ── Signature + diffusion d'une transaction (sérialisée par LI.FI / construite localement) ──

  async startSignAndSend(txBytesBase64: string, reqId: string): Promise<void> {
    const session = await this.hasActiveSession();
    if (!session) throw new Error('no_solana_deeplink_session');

    const sharedSecret = bs58.decode(session.sharedSecret);
    const nonce = nacl.randomBytes(24);
    const payloadJson = JSON.stringify({
      session: session.session,
      transaction: bs58.encode(Buffer.from(txBytesBase64, 'base64')),
    });
    const encrypted = nacl.box.after(Buffer.from(payloadJson, 'utf8'), nonce, sharedSecret);

    // Réutilise la paire de clés de la connexion (le wallet a lié la session à celle-ci).
    const dappKeyPair = nacl.box.keyPair.fromSecretKey(bs58.decode(session.dappSecretKey));

    await this.savePending({
      wallet: session.wallet,
      phase: 'sign',
      dappSecretKey: session.dappSecretKey,
      nonce: bs58.encode(nonce),
      reqId,
      returnUrl: window.location.pathname + window.location.search,
      createdAt: Date.now(),
    });

    const params = new URLSearchParams({
      dapp_encryption_public_key: bs58.encode(dappKeyPair.publicKey),
      nonce: bs58.encode(nonce),
      redirect_link: this.redirectLink(),
      payload: bs58.encode(encrypted),
    });
    this.openUrl(`${UL_BASE[session.wallet]}/signAndSendTransaction?${params.toString()}`);
  }

  // ── Traitement de la réponse (appelé par /solana-callback web, ou app.component en natif) ──

  async resolveCallback(params: Record<string, string>): Promise<void> {
    const pendingRaw = await Preferences.get({ key: PENDING_KEY });
    if (!pendingRaw.value) return;
    const pending = JSON.parse(pendingRaw.value) as PendingRequest;

    if (params['errorCode'] || params['errorMessage']) {
      await Preferences.remove({ key: PENDING_KEY });
      this.failed$.next({ phase: pending.phase, message: params['errorMessage'] || 'wallet_error' });
      return;
    }

    const dappSecretKey = bs58.decode(pending.dappSecretKey);

    if (pending.phase === 'connect') {
      const walletPubEnc = params['phantom_encryption_public_key'] || params['solflare_encryption_public_key'];
      const sharedSecret = nacl.box.before(bs58.decode(walletPubEnc), dappSecretKey);
      const decrypted = nacl.box.open.after(
        bs58.decode(params['data']),
        bs58.decode(params['nonce']),
        sharedSecret,
      );
      if (!decrypted) { this.failed$.next({ phase: 'connect', message: 'decrypt_failed' }); return; }
      const payload = JSON.parse(Buffer.from(decrypted).toString('utf8')) as { public_key: string; session: string };

      const dlSession: DeeplinkSession = {
        wallet: pending.wallet,
        publicKey: payload.public_key,
        session: payload.session,
        sharedSecret: bs58.encode(sharedSecret),
        dappSecretKey: pending.dappSecretKey,
      };
      await Preferences.set({ key: SESSION_KEY, value: JSON.stringify(dlSession) });
      await Preferences.remove({ key: PENDING_KEY });
      this.connected$.next(payload.public_key);
      return;
    }

    // phase === 'sign'
    const session = await this.hasActiveSession();
    if (!session) return;
    const sharedSecret = bs58.decode(session.sharedSecret);
    const decrypted = nacl.box.open.after(
      bs58.decode(params['data']),
      bs58.decode(params['nonce']),
      sharedSecret,
    );
    await Preferences.remove({ key: PENDING_KEY });
    if (!decrypted) { this.failed$.next({ phase: 'sign', message: 'decrypt_failed' }); return; }
    const payload = JSON.parse(Buffer.from(decrypted).toString('utf8')) as { signature: string };
    this.signature$.next({ reqId: pending.reqId, signature: payload.signature });
  }

  // ── Interne ────────────────────────────────────────────────────────────────

  private async savePending(p: PendingRequest): Promise<void> {
    await Preferences.set({ key: PENDING_KEY, value: JSON.stringify(p) });
  }

  private newReqId(): string {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  private openUrl(url: string): void {
    if (this.isNative) {
      void Browser.open({ url });
      return;
    }
    // Web mobile : nouvel onglet pour préserver l'état de la page d'origine. Si bloqué (rare,
    // l'appel vient d'un geste utilisateur), repli sur une navigation directe — la page se
    // réhydrate au retour via SolanaBridgeService.getPersistedConnection().
    const opened = window.open(url, '_blank');
    if (!opened) window.location.href = url;
  }
}
